// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// TRAILS — a connected scatterplot, one trail per region.
//
// The whole site argues that two quantities came apart. Every other view puts
// them side by side against time; this one puts them against EACH OTHER and
// lets time be the path. x is what the land is worth, y is what the farming
// takes in, and a region's trail is where it walked between 1990 and 2023-24.
//
// Two decisions make the geometry mean something, and both are load-bearing:
//
//   * BOTH AXES ARE LOG, AND BOTH CARRY THE SAME NUMBER OF PIXELS PER DECADE.
//     Only then is a 45-degree trail a trail that grew in step. If the axes had
//     different scales — the default outcome of drawing a wide chart — the
//     diagonal reference lines below would be decorative rather than true, and
//     every reader who measured an angle would be measuring our aspect ratio.
//
//   * The faint diagonals are lines of CONSTANT land-to-receipts ratio. Walking
//     along one means the land and the farming grew together. Crossing them
//     rightward is the entire finding, and it is visible as a direction rather
//     than as a number the reader has to trust us about.
//
// Colour here is the agro-climatic ZONE and nothing else. Teal and ochre are
// spent on the two axis rules — the axes ARE the farming and the land — and are
// not available to mean anything else on this page.

import { loadCore, loadMeta } from '../data';
import type { Zone } from '../types';
import { FARMING, GRAPHITE, LAND, NEUTRAL, SCOPE_SHORT, ZONE_COLOUR, ZONE_ORDER } from '../theme';
import {
  TIER_WORD, collapse, evidenceLines, isGap, isOutlineOnly, isRankable,
  isTokenSized, tierOpacity, tierPrefix, type Tier,
} from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartSvg, el, extent, finite, hatchDefs,
  isolatedPoints, logScale, markTip, niceTicks, type Scale,
} from '../components/svg';
import { esc, fy, money, num, pct, truncEsc } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { closeButton, openOverlay } from '../overlay';
import { currentRoute, setParams } from '../router';
import { attachSvgZoom } from '../utils/svgZoom';

/* ───────────────────────── local shapes over the real JSON ─────────────────
 *
 * core.json carries an evidence `tier` on every region and every region-year;
 * src/types.ts still describes the earlier `rel` wording. Rather than edit a
 * shared file from inside one view, this module declares the fields it reads
 * and casts once, at the door.
 */

interface TrailYear {
  year: number;
  tier: Tier | null;
  sample: number | null;
  rateExRse: number | null;
  landValue: number | null;
  receipts: number | null;
  area: number | null;
}

interface TrailRegion {
  name: string;
  zone: Zone;
  state: string | null;
  /** The region's OWN first observed year. Three NT regions start in 1992. */
  firstYear: number;
  tier: Tier | null;
  medianSample: number | null;
  weakYears: number;
  meanRateEx: number | null;
  landShare: number | null;
  series: TrailYear[];
}

type Measure = 'farm' | 'ha';

interface Pt {
  year: number;
  vx: number | null;
  vy: number | null;
  tier: Tier | null;
  sample: number | null;
  rse: number | null;
}

interface PxPt { x: number; y: number | null; p: Pt }

interface Drift {
  region: TrailRegion;
  /** How many times faster the land grew than the receipts did. */
  d: number;
  land: number;
  rec: number;
  from: TrailYear;
  to: TrailYear;
}

/* ──────────────────────────────── geometry ─────────────────────────────── */

const M = { top: 26, right: 30, bottom: 62, left: 74 };
/** A SQUARE plot, because equal pixels-per-decade is what makes 45° mean parity. */
const PLOT = 540;
const W = M.left + PLOT + M.right;
const H = M.top + PLOT + M.bottom;
const X0 = M.left;
const X1 = M.left + PLOT;
const Y0 = M.top;
const Y1 = M.top + PLOT;

const RANK_W = 660;
const RANK_BAR0 = 206;
const RANK_BAR1 = 566;
const RANK_ROW = 26;

export async function renderTrails(host: HTMLElement): Promise<void> {
  host.innerHTML = '<p class="loading">Loading the survey…</p>';

  const [core, meta] = await Promise.all([loadCore(), loadMeta()]);
  const regions = core.regions as unknown as TrailRegion[];
  const settledYears = core.settledYears;
  const settledLast = settledYears[settledYears.length - 1];
  const provisional = core.provisionalYear;
  const perFarm = (meta.headline as unknown as {
    perFarm: { fromYear: number; toYear: number; land: number; receipts: number; area: number };
    perHectare: { land: number; receipts: number };
  });

  /* ── state, seeded from the URL so a focused trail is linkable ───────── */
  const params = currentRoute().params;
  let measure: Measure = params.get('measure') === 'ha' ? 'ha' : 'farm';
  let zoneFilter: string = ZONE_ORDER.includes(params.get('zone') ?? '') ? String(params.get('zone')) : 'all';
  let focus: string | null = regions.some((r) => r.name === params.get('region')) ? params.get('region') : null;
  let showNational = params.get('nat') !== '0';

  /* ── the national reference trail, from the published national aggregate ─ */
  const nationalPts = (yearsWanted: number[], m: Measure): Pt[] => core.national
    .filter((n) => yearsWanted.includes(n.year))
    .map((n) => {
      const a = m === 'ha' ? n.area : 1;
      const ok = a != null && Number.isFinite(a) && a > 0;
      const div = ok ? (a as number) : 1;
      return {
        year: n.year,
        vx: ok && n.landValue != null && n.landValue > 0 ? n.landValue / div : null,
        vy: ok && n.receipts != null && n.receipts > 0 ? n.receipts / div : null,
        // The national rows carry no tier of their own; they are ABARES'
        // published aggregate, not one of our means. Drawn as structure, and
        // its tooltip says "sample size not published" rather than inventing one.
        tier: null, sample: null, rse: null,
      };
    });

  /* ── the shell, built once; draw() refills only the parts that change ─── */
  host.innerHTML = shellHtml(meta.regionCount, settledLast, perFarm);

  const segMeasure = host.querySelector<HTMLElement>('[data-seg="measure"]')!;
  const segZone = host.querySelector<HTMLElement>('[data-seg="zone"]')!;
  const selRegion = host.querySelector<HTMLSelectElement>('[data-sel="region"]')!;
  const focusPill = host.querySelector<HTMLElement>('[data-slot="focus-pill"]')!;
  const chartCard = host.querySelector<HTMLElement>('[data-slot="chart-card"]')!;
  const chartFrame = host.querySelector<HTMLElement>('[data-slot="chart-frame"]')!;
  const chartSub = host.querySelector<HTMLElement>('[data-slot="chart-sub"]')!;
  const legendSlot = host.querySelector<HTMLElement>('[data-slot="legend"]')!;
  const readout = host.querySelector<HTMLElement>('[data-slot="readout"]')!;
  const rankFrame = host.querySelector<HTMLElement>('[data-slot="rank-frame"]')!;
  const rankNote = host.querySelector<HTMLElement>('[data-slot="rank-note"]')!;
  const backNote = host.querySelector<HTMLElement>('[data-slot="back-note"]')!;

  selRegion.innerHTML = `<option value="">Every region</option>${[...regions]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((r) => `<option value="${esc(r.name)}">${esc(r.name)}</option>`).join('')}`;

  /* One delegated listener per control, all on nodes INSIDE host, so they are
     collected with the host on the next navigation. Nothing binds document. */
  segMeasure.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('button[data-v]');
    if (!b) return;
    measure = b.dataset.v === 'ha' ? 'ha' : 'farm';
    sync();
  });
  segZone.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('button[data-v]');
    if (!b) return;
    zoneFilter = b.dataset.v ?? 'all';
    sync();
  });
  selRegion.addEventListener('change', () => { focus = selRegion.value || null; sync(); });
  focusPill.addEventListener('click', () => { focus = null; sync(); });

  function setFocus(name: string | null): void {
    focus = focus === name ? null : name;
    sync();
  }

  function scrollToChart(): void {
    const top = chartCard.getBoundingClientRect().top + window.scrollY - 110;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
  }

  function sync(): void {
    setParams({
      measure: measure === 'ha' ? 'ha' : null,
      zone: zoneFilter === 'all' ? null : zoneFilter,
      region: focus,
      nat: showNational ? null : '0',
    });
    draw();
  }

  /* ───────────────────────────── the draw ──────────────────────────────── */

  function draw(): void {
    for (const b of segMeasure.querySelectorAll<HTMLButtonElement>('button[data-v]')) {
      b.setAttribute('aria-pressed', String(b.dataset.v === measure));
    }
    for (const b of segZone.querySelectorAll<HTMLButtonElement>('button[data-v]')) {
      b.setAttribute('aria-pressed', String(b.dataset.v === zoneFilter));
    }
    selRegion.value = focus ?? '';
    focusPill.hidden = focus == null;
    if (focus) focusPill.innerHTML = `<span class="truncate">Following ${esc(focus)}</span><span aria-hidden="true">✕</span>`;

    const shown = regions.filter((r) => zoneFilter === 'all' || r.zone === zoneFilter);

    const unit = measure === 'farm' ? 'per surveyed farm' : 'per hectare operated';
    chartSub.textContent = `Each thread is one region walking from its own first surveyed year to ${fy(settledLast)}. `
      + `Rightward means the land got dearer; upward means the farming took in more. Both axes are logarithmic and carry the `
      + `same distance per tenfold, so a thread climbing at 45° is a region where the two grew in step. Values are ${unit}, in `
      + `nominal dollars.`;

    drawTrails(shown);
    drawRanking(shown);
  }

  /* ── the trail chart ─────────────────────────────────────────────────── */

  function drawTrails(shown: TrailRegion[]): void {
    chartFrame.innerHTML = '';
    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    chartFrame.append(scroll);

    const svg = chartSvg(W, H, `Land value against cash receipts, ${measure === 'farm' ? 'per surveyed farm' : 'per hectare'}, `
      + `for ${shown.length} broadacre regions, 1990 to ${fy(settledLast)}`);
    // The site's charts otherwise shrink to fit; this one must not, or the 11px
    // axis text lands at 6px on a phone. It scrolls inside .chart-scroll instead.
    svg.style.width = `${W}px`;
    svg.style.minWidth = `${W}px`;
    svg.style.maxWidth = 'none';
    svg.style.margin = '0 auto';
    svg.append(hatchDefs('trails-prov-hatch', GRAPHITE, 0.75));

    /* Gather every value that will be drawn, so both axes can be sized.
       Points are built over ALL years including the provisional one — it still
       gets drawn, as a dashed spur, so the axis has to reach it. Everything that
       is averaged, ranked or fitted filters back to the settled years first. */
    const series = shown.map((r) => ({ r, pts: pointsFor(r.series, core.years, measure) }));
    // The national reference stops at the last SETTLED year: it is the anchor the
    // rest of the chart is read against, and an anchor should not be provisional.
    const natSettled = nationalPts(settledYears, measure);
    const xs: (number | null)[] = [];
    const ys: (number | null)[] = [];
    for (const s of series) for (const p of s.pts) { xs.push(p.vx); ys.push(p.vy); }
    if (showNational) for (const p of natSettled) { xs.push(p.vx); ys.push(p.vy); }
    if (!xs.some((v) => v != null)) {
      scroll.innerHTML = '<p class="empty-state">No region in this zone has both figures.</p>';
      legendSlot.innerHTML = '';
      readout.innerHTML = '';
      return;
    }

    const [sx, sy] = squareLogScales(xs, ys);

    /* Reference diagonals: constant land-to-receipts ratio. */
    const gRef = el('g');
    for (const k of ratioLines(xs, ys)) {
      const seg = diagonal(k, sx, sy);
      if (!seg) continue;
      gRef.append(el('line', {
        x1: seg.x1, y1: seg.y1, x2: seg.x2, y2: seg.y2,
        stroke: GRAPHITE, 'stroke-width': 1, opacity: 0.16, 'stroke-dasharray': '1 5',
      }));
      // Set ALONG the diagonal, not at its end: every one of these lines exits
      // through the same top edge, so end-labels stack on top of each other and
      // become one unreadable smear. Rotated -45° is exact here — equal
      // pixels-per-decade on both axes is what makes these lines 45° at all.
      const mx = (seg.x1 + seg.x2) / 2;
      const my = (seg.y1 + seg.y2) / 2;
      const label = el('text', {
        x: 0, y: -5, 'text-anchor': 'middle',
        transform: `translate(${finite(mx).toFixed(2)},${finite(my).toFixed(2)}) rotate(-45)`,
        class: 'axis-text', opacity: 0.9, 'paint-order': 'stroke',
        stroke: 'var(--bg-panel)', 'stroke-width': 3.5,
      });
      label.textContent = `land = ${formatRatio(k)}× receipts`;
      gRef.append(label);
    }
    svg.append(gRef);

    const fmt = (v: number) => (measure === 'farm' ? money(v) : (v >= 100 ? `$${num(v)}` : `$${v.toFixed(1)}`));
    axisLeft(svg, sy, X0, { ticks: thin(sy.ticks()), format: fmt, gridTo: X1 });
    axisBottom(svg, sx, Y1, { ticks: thin(sx.ticks()), format: fmt, x0: X0, x1: X1 });

    // Teal down the vertical axis, ochre along the horizontal one: on this chart
    // the AXES are the two quantities, so that is where the two hues live.
    svg.append(el('rect', { x: X0 - 6, y: Y0, width: 3, height: PLOT, fill: FARMING, opacity: 0.9 }));
    svg.append(el('rect', { x: X0, y: Y1 + 3, width: PLOT, height: 3, fill: LAND, opacity: 0.9 }));

    const yTitle = el('text', {
      class: 'axis-title', fill: FARMING, 'text-anchor': 'middle',
      transform: `translate(${X0 - 56},${(Y0 + Y1) / 2}) rotate(-90)`,
    });
    yTitle.textContent = 'Total cash receipts →';
    svg.append(yTitle);
    const xTitle = el('text', { class: 'axis-title', fill: LAND, x: (X0 + X1) / 2, y: Y1 + 50, 'text-anchor': 'middle' });
    xTitle.textContent = 'Value of land and fixed improvements →';
    svg.append(xTitle);

    /* Trails, unfocused first so the focused one lands on top. */
    const gTrails = el('g');
    const gFront = el('g');
    svg.append(gTrails, gFront);

    for (const { r, pts } of series) {
      const focused = focus === r.name;
      const target = focused ? gFront : gTrails;
      target.append(trailGroup(r, pts, sx, sy, focused, shown.length));
    }

    if (showNational) gFront.append(nationalGroup(natSettled, sx, sy));

    scroll.append(svg);
    // Must be attached AFTER the svg is in the DOM: attachSvgZoom hangs its
    // controls off svg.parentElement and silently renders nothing otherwise.
    //
    // The guard is for jsdom, which does not implement SVGSVGElement.viewBox at
    // all — the helper reads viewBox.baseVal on its first line and throws, which
    // would take the whole view down inside the test suite while working
    // perfectly in every browser.
    if (svg.viewBox?.baseVal) {
      attachSvgZoom(svg, { maxScale: 12 });
      // The canonical helper sets touch-action:none, which on a phone also eats
      // the VERTICAL page scroll whenever a finger lands on the chart. pan-y
      // hands vertical back to the browser and keeps horizontal drag for panning.
      svg.style.touchAction = 'pan-y';
    }

    drawLegend(shown);
    drawReadout(shown, series, natSettled);
  }

  function trailGroup(
    r: TrailRegion, pts: Pt[], sx: Scale, sy: Scale, focused: boolean, shownCount: number,
  ): SVGGElement {
    const g = el('g');
    const hue = collapse(ZONE_COLOUR[r.zone] ?? NEUTRAL, r.tier);
    const baseOp = tierOpacity(r.tier);
    const dim = focus != null && !focused;
    const op = dim ? baseOp * 0.16 : baseOp * (shownCount > 18 ? 0.85 : 1);
    const width = focused ? 3.2 : 1.5;

    const px = toPixels(pts, sx, sy);
    const settled = px.filter((q) => q.p.year <= settledLast);

    for (const d of brokenPath(settled)) {
      g.append(el('path', { d, fill: 'none', stroke: hue, 'stroke-width': width, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', opacity: op }));
    }
    // A year that survives alone between two gaps would otherwise be invisible.
    for (const q of isolatedPoints(settled)) {
      g.append(el('circle', { cx: finite(q.x), cy: finite(q.y), r: 2.6, fill: hue, opacity: op }));
    }

    const live = settled.filter((q) => q.y != null);
    const head = live[live.length - 1];
    const tail = live[0];

    /* The provisional year is a DASHED spur off the settled head and a hollow
       hatched marker. It is in no mean, no ranking and no fitted line here. */
    const prov = px.find((q) => provisional != null && q.p.year === provisional && q.y != null);
    if (head && prov && prov.y != null) {
      g.append(el('path', {
        d: `M${finite(head.x).toFixed(2)},${finite(head.y).toFixed(2)} L${finite(prov.x).toFixed(2)},${finite(prov.y).toFixed(2)}`,
        fill: 'none', stroke: hue, 'stroke-width': width, 'stroke-dasharray': '3 4', opacity: op * 0.75,
      }));
      const pm = el('rect', {
        x: finite(prov.x) - 4, y: finite(prov.y) - 4, width: 8, height: 8,
        fill: 'url(#trails-prov-hatch)', stroke: hue, 'stroke-width': 1.1, opacity: op * 0.8,
      });
      markTip(pm, [
        `${esc(r.name)} · ${fy(provisional as number)} (provisional)`,
        `Land ${valueText(prov.p.vx)} · receipts ${valueText(prov.p.vy)}`,
        'Provisional — excluded from every mean, trend and ranking on this page.',
        ...evidenceLines(prov.p.sample, prov.p.tier, prov.p.rse),
      ]);
      g.append(pm);
    }

    if (tail && tail.y != null) {
      const ring = el('circle', { cx: finite(tail.x), cy: finite(tail.y), r: 4.2, fill: 'var(--bg-panel)', stroke: hue, 'stroke-width': 1.6, opacity: op });
      markTip(ring, [
        `${esc(r.name)} starts here, ${fy(tail.p.year)}`,
        `Land ${valueText(tail.p.vx)} · receipts ${valueText(tail.p.vy)}`,
        ...evidenceLines(tail.p.sample, tail.p.tier, tail.p.rse),
      ]);
      ring.addEventListener('click', () => setFocus(r.name));
      g.append(ring);
    }

    if (head && head.y != null) {
      const prev = live[live.length - 2];
      if (prev && prev.y != null) {
        const arrow = arrowHead(finite(prev.x), finite(prev.y), finite(head.x), finite(head.y), hue, op);
        if (arrow) g.append(arrow);
      }
      const dot = el('circle', { cx: finite(head.x), cy: finite(head.y), r: focused ? 5 : 3.6, fill: hue, opacity: op });
      markTip(dot, [
        `${esc(r.name)} in ${fy(head.p.year)}`,
        `Land ${valueText(head.p.vx)} · receipts ${valueText(head.p.vy)}`,
        ...evidenceLines(head.p.sample, head.p.tier, head.p.rse),
      ]);
      dot.addEventListener('click', () => setFocus(r.name));
      g.append(dot);
    }

    /* Per-year waypoints only for the followed region — 32 regions × 35 years of
       dots is a fog, and the unfocused trails already say everything by shape. */
    if (focused) {
      for (const q of settled) {
        if (q.y == null || isGap(q.p.tier)) continue;
        const node = waypoint(finite(q.x), finite(q.y), q.p.tier, hue);
        markTip(node, [
          `${esc(r.name)}, ${fy(q.p.year)}`,
          `Land ${valueText(q.p.vx, q.p.tier)} · receipts ${valueText(q.p.vy, q.p.tier)}`,
          q.p.vx != null && q.p.vy != null ? `Land is ${formatRatio(q.p.vx / q.p.vy)}× the year's receipts` : null,
          ...evidenceLines(q.p.sample, q.p.tier, q.p.rse),
          'Open this region year by year →',
        ]);
        node.setAttribute('tabindex', '0');
        node.setAttribute('role', 'button');
        node.style.cursor = 'pointer';
        node.addEventListener('click', () => openRegion(r));
        node.addEventListener('keydown', enterSpace(() => openRegion(r)));
        g.append(node);
      }
    }

    /* A 1.5px line is not a hover target. An invisible fat stroke over the top
       carries the tooltip, the click and the keyboard stop for the whole trail. */
    const d = drift(r, settledLast);
    const hit = el('g', { class: 'mark' });
    for (const seg of brokenPath(settled)) {
      hit.append(el('path', { d: seg, fill: 'none', stroke: 'transparent', 'stroke-width': 14, 'pointer-events': 'stroke' }));
    }
    markTip(hit, [
      esc(r.name),
      d ? `Land grew ${tierPrefix(r.tier)}${formatRatio(d.land)}× while receipts grew ${formatRatio(d.rec)}× — the land pulled ahead ${formatRatio(d.d)}×`
        : 'Not enough of both figures to compare the two ends.',
      `${r.zone} zone${r.state ? ` · ${r.state}` : ''} · first surveyed ${fy(r.firstYear)}`,
      ...evidenceLines(r.medianSample, r.tier),
      focus === r.name ? 'Click to stop following this region' : 'Click to follow this region',
    ]);
    hit.setAttribute('tabindex', '0');
    hit.setAttribute('role', 'button');
    hit.style.cursor = 'pointer';
    hit.addEventListener('click', () => setFocus(r.name));
    hit.addEventListener('keydown', enterSpace(() => setFocus(r.name)));
    g.append(hit);
    return g;
  }

  function nationalGroup(pts: Pt[], sx: Scale, sy: Scale): SVGGElement {
    const g = el('g');
    const px = toPixels(pts, sx, sy);
    const op = focus != null ? 0.35 : 1;
    for (const d of brokenPath(px)) {
      // A pale casing under the line so it reads over the crowd without needing
      // a colour of its own — the national trail is structure, not a measure.
      g.append(el('path', { d, fill: 'none', stroke: 'var(--bg-panel)', 'stroke-width': 6, 'stroke-linecap': 'round', opacity: op }));
      g.append(el('path', { d, fill: 'none', stroke: GRAPHITE, 'stroke-width': 2.6, 'stroke-linecap': 'round', opacity: op }));
    }
    const live = px.filter((q) => q.y != null);
    for (const q of live) {
      if (q.p.year % 10 !== 0 && q.p.year !== settledLast && q.p.year !== 1990) continue;
      const node = el('circle', { cx: finite(q.x), cy: finite(q.y), r: 3.4, fill: GRAPHITE, stroke: 'var(--bg-panel)', 'stroke-width': 1.2, opacity: op });
      markTip(node, [
        `All surveyed broadacre farms, ${fy(q.p.year)}`,
        `Land ${valueText(q.p.vx)} · receipts ${valueText(q.p.vy)}`,
        q.p.vx != null && q.p.vy != null ? `Land is ${formatRatio(q.p.vx / q.p.vy)}× the year's receipts` : null,
        ...evidenceLines(q.p.sample, q.p.tier),
        "ABARES' published national aggregate, not a mean taken here.",
      ]);
      g.append(node);
    }
    const head = live[live.length - 1];
    if (head) {
      const t = el('text', { x: finite(head.x) + 9, y: finite(head.y) - 7, class: 'axis-title', fill: GRAPHITE, opacity: op, 'paint-order': 'stroke', stroke: 'var(--bg-panel)', 'stroke-width': 3 });
      t.textContent = 'all surveyed farms';
      g.append(t);
    }
    return g;
  }

  /* ── legend ──────────────────────────────────────────────────────────── */

  function drawLegend(shown: TrailRegion[]): void {
    const counts = new Map<string, number>();
    for (const r of shown) counts.set(r.zone, (counts.get(r.zone) ?? 0) + 1);

    const zoneItems = ZONE_ORDER.map((z) => {
      const on = zoneFilter === 'all' || zoneFilter === z;
      return `<button type="button" class="legend-item" data-zone="${esc(z)}" aria-pressed="${zoneFilter === z}"
        data-tip="${esc(`Show only the ${z} zone`)}" style="opacity:${on ? 1 : 0.45}">
        <span class="legend-swatch" style="background:${ZONE_COLOUR[z]}"></span>
        <span>${esc(z)}${counts.has(z) ? ` (${counts.get(z)})` : ''}</span></button>`;
    }).join('');

    legendSlot.innerHTML = `
      <div class="legend">${zoneItems}
        <button type="button" class="legend-item" data-nat="1" aria-pressed="${showNational}"
          data-tip="Show or hide the trail for all surveyed broadacre farms together">
          <span class="legend-swatch" style="background:${GRAPHITE};border-radius:1px;height:4px"></span>
          <span>All surveyed farms together</span></button>
      </div>
      <div class="legend">
        <span class="legend-item"><span class="legend-swatch" style="background:${LAND}"></span><span>horizontal axis — what the land is worth</span></span>
        <span class="legend-item"><span class="legend-swatch" style="background:${FARMING}"></span><span>vertical axis — what the farming takes in</span></span>
        <span class="legend-item muted">Colour is the ${glossTerm('zone', 'agro-climatic zone')}. Nothing here is coloured by how big a number is.</span>
      </div>
      <div class="legend" data-slot="tierkey">
        <span class="legend-item"><svg width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="3.2" fill="${GRAPHITE}"></circle></svg><span>${TIER_WORD[1]} / ${TIER_WORD[2]} — solid dot</span></span>
        <span class="legend-item"><svg width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="3.4" fill="none" stroke="${GRAPHITE}" stroke-width="1.4" opacity="0.6"></circle></svg><span>${TIER_WORD[3]} — hollow, and read “~” before the number</span></span>
        <span class="legend-item"><svg width="14" height="14" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" fill="none" stroke="${GRAPHITE}" stroke-width="1.2" opacity="0.45"></rect></svg><span>${TIER_WORD[4]} — fixed token, never sized by the value</span></span>
        <span class="legend-item"><svg width="26" height="14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="${GRAPHITE}" stroke-width="2" stroke-dasharray="3 4"></line></svg><span>${provisional != null ? fy(provisional) : 'latest year'} — provisional, in nothing that is averaged or ranked</span></span>
      </div>`;

    legendSlot.querySelectorAll<HTMLButtonElement>('[data-zone]').forEach((b) => {
      b.addEventListener('click', () => {
        const z = b.dataset.zone ?? 'all';
        zoneFilter = zoneFilter === z ? 'all' : z;
        sync();
      });
    });
    legendSlot.querySelector<HTMLButtonElement>('[data-nat]')?.addEventListener('click', () => {
      showNational = !showNational;
      sync();
    });
  }

  /* ── the readout under the chart ─────────────────────────────────────── */

  function drawReadout(
    shown: TrailRegion[],
    series: Array<{ r: TrailRegion; pts: Pt[] }>,
    nat: Pt[],
  ): void {
    if (focus) {
      const entry = series.find((s) => s.r.name === focus);
      if (!entry) { readout.innerHTML = ''; return; }
      const r = entry.r;
      const d = drift(r, settledLast);
      const rankable = isRankable(r.tier);
      readout.innerHTML = `
        <div class="row" style="flex-wrap:wrap;gap:var(--space-md)">
          <div class="grow">
            <div class="card-title truncate">${esc(r.name)}</div>
            <div class="card-sub">${esc(r.zone)} zone${r.state ? ` · ${esc(r.state)}` : ''} · first surveyed ${fy(r.firstYear)}
              · evidence: ${esc(TIER_WORD[(r.tier ?? 5) as Tier])}${rankable ? '' : ' — held out of the ranking below'}</div>
          </div>
          <button type="button" class="btn btn-sm" data-act="open-region">See every year</button>
        </div>
        <div class="stat-row" style="margin-top:var(--space-md)">
          <div class="stat"><div class="stat-value land">${d ? `${tierPrefix(r.tier)}${formatRatio(d.land)}×` : '—'}</div>
            <div class="stat-label">Land, ${fy(r.firstYear)} → ${fy(settledLast)}</div>
            <div class="stat-note">${d ? `${money(d.from.landValue)} → ${money(d.to.landValue)} per farm` : 'not comparable'}</div></div>
          <div class="stat"><div class="stat-value farming">${d ? `${tierPrefix(r.tier)}${formatRatio(d.rec)}×` : '—'}</div>
            <div class="stat-label">Cash receipts, same span</div>
            <div class="stat-note">${d ? `${money(d.from.receipts)} → ${money(d.to.receipts)} per farm` : 'not comparable'}</div></div>
          <div class="stat"><div class="stat-value">${d ? `${formatRatio(d.d)}×` : '—'}</div>
            <div class="stat-label">How far the land pulled ahead</div>
            <div class="stat-note">${r.landShare != null ? `${pct(r.landShare, 0)} of this region's total return came from the land` : 'land share not available'}</div></div>
        </div>`;
      readout.querySelector<HTMLButtonElement>('[data-act="open-region"]')?.addEventListener('click', () => openRegion(r));
      return;
    }

    const a = nat.find((p) => p.year === 1990);
    const b = nat.find((p) => p.year === settledLast);
    const withDrift = shown.map((r) => drift(r, settledLast)).filter((x): x is Drift => x != null);
    const ahead = withDrift.filter((x) => isRankable(x.region.tier) && x.d > 1).length;
    const rankableCount = withDrift.filter((x) => isRankable(x.region.tier)).length;
    readout.innerHTML = `
      <p class="small muted">Following nothing in particular — click any thread, or pick a region above.
        ${a && b && a.vx != null && b.vx != null && a.vy != null && b.vy != null
          ? `Across all surveyed broadacre farms together the trail ran from ${valueText(a.vx)} of land against ${valueText(a.vy)} of receipts in ${fy(1990)},
             to ${valueText(b.vx)} against ${valueText(b.vy)} in ${fy(settledLast)}.` : ''}
        ${rankableCount ? `Of the ${rankableCount} region${rankableCount === 1 ? '' : 's'} in view with firm or fair evidence, the land outgrew the receipts in ${ahead}.` : ''}</p>`;
  }

  /* ── the ranking ─────────────────────────────────────────────────────── */

  function drawRanking(shown: TrailRegion[]): void {
    rankFrame.innerHTML = '';

    const all = shown.map((r) => drift(r, settledLast)).filter((x): x is Drift => x != null);
    // A RANKING takes tier 1 and 2 only. The region tier is ABARES' evidence
    // summarised across all 35 settled years, which is the right grain for a
    // statement about a whole region over the whole period.
    const rows = all.filter((x) => isRankable(x.region.tier)).sort((p, q) => q.d - p.d);
    const held = all.filter((x) => !isRankable(x.region.tier));

    rankNote.innerHTML = `<p class="note"><strong>${held.length} of the ${all.length} region${all.length === 1 ? '' : 's'} in view
      ${held.length === 1 ? 'is' : 'are'} not ranked here.</strong> Their survey evidence is weak or worse across the period
      (${esc(TIER_WORD[3])} or ${esc(TIER_WORD[4])}), and a league table built on estimates that cannot be read as numbers is a
      league table of noise. ${held.length ? '<button type="button" class="btn btn-sm" data-act="held" style="margin-top:var(--space-sm)">Which regions, and why</button>' : ''}</p>
      <p class="small muted">Dividing by area cannot change this number: the hectares cancel out of a ratio of two growth ratios.
      The gap is not an artefact of the surveyed farms getting bigger — they grew ${formatRatio(perFarm.perFarm.area)}× in area
      over the same span, and the land still ran ${formatRatio(perFarm.perHectare.land / perFarm.perHectare.receipts)}× ahead per hectare.</p>`;
    rankNote.querySelector<HTMLButtonElement>('[data-act="held"]')?.addEventListener('click', () => openHeld(held));

    if (!rows.length) {
      rankFrame.innerHTML = '<p class="empty-state">No region in this zone has firm or fair evidence across the whole period.</p>';
      return;
    }

    const dmin = Math.min(1, ...rows.map((x) => x.d));
    const dmax = Math.max(1, ...rows.map((x) => x.d));
    const lo = dmin - (dmax - dmin) * 0.06;
    const hi = dmax + (dmax - dmin) * 0.06;
    const s = linearRank(lo, hi);
    const height = 44 + rows.length * RANK_ROW + 22;

    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    const svg = chartSvg(RANK_W, height, `How many times faster land value grew than cash receipts, ${rows.length} regions`);
    svg.style.width = `${RANK_W}px`;
    svg.style.minWidth = `${RANK_W}px`;
    svg.style.maxWidth = 'none';

    /* Axis anchored at 1× — parity — never at the middle of the data. */
    const ticks = niceTicks(lo, hi, 5);
    const gAxis = el('g', { class: 'axis' });
    for (const t of ticks) {
      const x = s(t);
      if (!Number.isFinite(x)) continue;
      gAxis.append(el('line', { class: 'grid-line', x1: x, y1: 34, x2: x, y2: height - 22 }));
      const lab = el('text', { class: 'axis-text', x, y: 26, 'text-anchor': 'middle' });
      lab.textContent = `${formatRatio(t)}×`;
      gAxis.append(lab);
    }
    const parity = s(1);
    if (Number.isFinite(parity)) {
      gAxis.append(el('line', { class: 'zero-line', x1: parity, y1: 34, x2: parity, y2: height - 22 }));
      const pl = el('text', { class: 'axis-text', x: parity, y: 12, 'text-anchor': 'middle', fill: 'var(--text-secondary)' });
      pl.textContent = 'grew in step';
      gAxis.append(pl);
    }
    svg.append(gAxis);

    rows.forEach((x, i) => {
      const y = 44 + i * RANK_ROW;
      const cy = y + RANK_ROW / 2;
      const g = el('g', { class: 'mark' });

      const label = el('text', { x: 0, y: cy + 4, class: 'axis-text', fill: 'var(--text-primary)' });
      label.textContent = shorten(x.region.name);
      g.append(label);

      // Ochre when the land pulled ahead, teal when the farming kept up or beat
      // it — the two hues doing exactly their one job. Never crimson: a ratio of
      // two growth multiples is never below zero, so crimson would be a lie.
      const hue = collapse(x.d >= 1 ? LAND : FARMING, x.region.tier);
      const bx = s(x.d);
      const left = Math.min(parity, bx);
      const w = Math.abs(bx - parity);
      if (Number.isFinite(left) && Number.isFinite(w)) {
        const bar = el('rect', {
          x: finite(left), y: y + 5, width: Math.max(1.5, finite(w)), height: RANK_ROW - 10,
          fill: hue, opacity: tierOpacity(x.region.tier), rx: 2, 'pointer-events': 'none',
        });
        g.append(bar);
      }

      const val = el('text', { x: RANK_BAR1 + 12, y: cy + 4, class: 'axis-text', fill: 'var(--text-secondary)' });
      val.textContent = `${tierPrefix(x.region.tier)}${formatRatio(x.d)}×`;
      g.append(val);

      const hit = el('rect', { x: 0, y, width: RANK_W, height: RANK_ROW, fill: 'transparent' });
      markTip(hit, [
        `${esc(x.region.name)}: the land pulled ahead ${tierPrefix(x.region.tier)}${formatRatio(x.d)}×`,
        `Land ×${formatRatio(x.land)}, cash receipts ×${formatRatio(x.rec)}, ${fy(x.from.year)} → ${fy(x.to.year)}`,
        `${x.region.zone} zone${x.region.state ? ` · ${x.region.state}` : ''}`,
        ...evidenceLines(x.region.medianSample, x.region.tier),
        'Click to follow this region on the chart above',
      ]);
      hit.setAttribute('tabindex', '0');
      hit.setAttribute('role', 'button');
      hit.style.cursor = 'pointer';
      const act = () => { focus = x.region.name; sync(); scrollToChart(); };
      hit.addEventListener('click', act);
      hit.addEventListener('keydown', enterSpace(act));
      g.append(hit);

      if (focus === x.region.name) {
        g.append(el('rect', { x: 0, y, width: RANK_W - 1, height: RANK_ROW, fill: 'none', stroke: 'var(--text-primary)', 'stroke-width': 1.4, rx: 3, 'pointer-events': 'none' }));
      }
      svg.append(g);
    });

    scroll.append(svg);
    rankFrame.append(scroll);
  }

  /* ── drill-downs ─────────────────────────────────────────────────────── */

  function openRegion(r: TrailRegion): void {
    openOverlay({
      panelClass: 'modal',
      label: `${r.name}, year by year`,
      lockScroll: true,
      build(panel, close) {
        const head = document.createElement('div');
        head.className = 'overlay-head';
        const h = document.createElement('h2');
        h.textContent = `${r.name} — every surveyed year`;
        head.append(h, closeButton(close, `Close ${r.name}`));

        const body = document.createElement('div');
        body.className = 'stack';
        const rowsHtml = r.series.map((y) => {
          const isProv = provisional != null && y.year === provisional;
          const ratio = y.landValue != null && y.receipts != null && y.receipts > 0 ? y.landValue / y.receipts : null;
          const t = y.tier;
          return `<tr>
            <td class="mono">${fy(y.year)}${isProv ? ' <span class="pill">provisional</span>' : ''}</td>
            <td class="num">${t != null ? tierPrefix(t) : ''}${money(y.landValue)}</td>
            <td class="num">${t != null ? tierPrefix(t) : ''}${money(y.receipts)}</td>
            <td class="num">${ratio != null ? `${formatRatio(ratio)}×` : '—'}</td>
            <td class="num">${y.area != null ? num(y.area) : '—'}</td>
            <td>${esc(TIER_WORD[(t ?? 5) as Tier])}</td>
            <td class="num">${y.sample != null ? num(y.sample) : '—'}</td>
          </tr>`;
        }).join('');
        body.innerHTML = `
          <p class="small muted">${esc(r.zone)} zone${r.state ? ` · ${esc(r.state)}` : ''} · first surveyed ${fy(r.firstYear)}
            · ${r.weakYears} of ${r.series.length} years carry weak evidence or worse.
            Figures are ${glossTerm('perFarm', 'per surveyed farm')} and ${glossTerm('nominal', 'in nominal dollars')}.</p>
          <div class="table-scroll"><table>
            <thead><tr><th>${glossTerm('financialYear', 'Year')}</th><th class="num">Land value</th><th class="num">Cash receipts</th>
              <th class="num">Land ÷ receipts</th><th class="num">Area (ha)</th><th>Evidence ${glossIcon('rse')}</th>
              <th class="num">Farms surveyed</th></tr></thead>
            <tbody>${rowsHtml}</tbody></table></div>
          <p class="xsmall muted">The ${provisional != null ? fy(provisional) : 'latest'} row is provisional and is excluded from the
            ranking, from the growth multiples above and from every average on this site. A “~” marks a year whose survey evidence
            is weak — read the direction, not the number.</p>`;
        panel.append(head, body);
      },
    });
  }

  function openHeld(held: Drift[]): void {
    openOverlay({
      panelClass: 'modal',
      label: 'Regions held out of the ranking',
      lockScroll: true,
      build(panel, close) {
        const head = document.createElement('div');
        head.className = 'overlay-head';
        const h = document.createElement('h2');
        h.textContent = 'Held out of the ranking';
        head.append(h, closeButton(close, 'Close'));
        const body = document.createElement('div');
        body.className = 'stack';
        const rowsHtml = held.sort((a, b) => b.d - a.d).map((x) => `<tr>
            <td>${esc(x.region.name)}</td>
            <td>${esc(TIER_WORD[(x.region.tier ?? 5) as Tier])}</td>
            <td class="num">${num(x.region.weakYears)}</td>
            <td class="num">${x.region.medianSample != null ? num(x.region.medianSample) : '—'}</td>
            <td class="num muted">~${formatRatio(x.d)}×</td>
          </tr>`).join('');
        body.innerHTML = `
          <p class="small">A region is ranked only where its evidence across the whole period is firm or fair. These
            ${held.length} are not. Their figures are shown greyed and tilde-marked because the direction is worth knowing
            and the number is not: every one of them points the same way as the ranked regions, but none of them is
            precise enough to place in an order.</p>
          <div class="table-scroll"><table>
            <thead><tr><th>Region</th><th>Evidence ${glossIcon('rse')}</th><th class="num">Weak years</th>
              <th class="num">Median ${glossTerm('sampleContributing', 'farms surveyed')}</th><th class="num">Land pulled ahead</th></tr></thead>
            <tbody>${rowsHtml}</tbody></table></div>`;
        panel.append(head, body);
      },
    });
  }

  /* ── the counterweight, stated where the chart can be checked against it ─ */
  const backwards = countBackwardSteps(regions, settledLast);
  backNote.innerHTML = `
    <p class="note warn"><strong>The threads are not one-way.</strong> In ${num(backwards.back)} of the
      ${num(backwards.total)} year-to-year steps drawn here the land value per surveyed farm was <em>lower</em> than the year
      before — the thread walks backwards. Nationally, ${glossTerm('capitalAppreciation', 'capital appreciation')} was negative in
      ${meta.headline.negativeAppreciationYears.length} of the ${core.years.length} years, in two clusters
      (${esc(clusters(meta.headline.negativeAppreciationYears))}). Because these are per-farm averages from a sample, a step
      backwards can mean prices fell, or simply that the farms in the sample changed.</p>`;

  draw();
}

/* ════════════════════════════ helpers ═══════════════════════════════════ */

function shellHtml(
  regionCount: number,
  settledLast: number,
  perFarm: { perFarm: { land: number; receipts: number; area: number }; perHectare: { land: number; receipts: number } },
): string {
  const ahead = perFarm.perFarm.land / perFarm.perFarm.receipts;
  return `
  <div class="view-head">
    <h1>Yield trails</h1>
    <p class="lede">Every other chart on this site puts the land and the farming side by side against time. This one puts them
      against each other, and lets time be the path. Each thread is one ${glossTerm('broadacre', 'broadacre')} region walking from
      its first surveyed year to ${fy(settledLast)}.</p>
    <p>If the land and the farming had grown together, the threads would climb at 45°. Almost none of them do.</p>
  </div>

  <div class="stat-row" style="margin-bottom:var(--space-xl)">
    <div class="stat" data-tip="Value of land and fixed improvements per surveyed farm, 1989–90 to 2023–24, from the published national aggregate.">
      <div class="stat-value land">${formatRatio(perFarm.perFarm.land)}×</div>
      <div class="stat-label">Land, per surveyed farm</div>
      <div class="stat-note">1989–90 → ${fy(settledLast)}</div></div>
    <div class="stat" data-tip="Total cash receipts per surveyed farm over the same span, from the published national aggregate.">
      <div class="stat-value farming">${formatRatio(perFarm.perFarm.receipts)}×</div>
      <div class="stat-label">Cash receipts, same span</div>
      <div class="stat-note">1989–90 → ${fy(settledLast)}</div></div>
    <div class="stat" data-tip="The ratio of the two multiples. Area cancels out of it, so it is the same figure per farm and per hectare.">
      <div class="stat-value">${formatRatio(ahead)}×</div>
      <div class="stat-label">How far the land pulled ahead</div>
      <div class="stat-note">The same ${formatRatio(perFarm.perHectare.land / perFarm.perHectare.receipts)}× per hectare — area cancels out of it</div></div>
  </div>

  <div class="controls">
    <div class="control"><span class="field-label">Measure</span>
      <div class="seg" data-seg="measure" role="group" aria-label="Measure">
        <button type="button" data-v="farm" aria-pressed="true">Per surveyed farm</button>
        <button type="button" data-v="ha" aria-pressed="false">Per hectare</button>
      </div></div>
    <div class="control"><span class="field-label">Zone ${glossIcon('zone')}</span>
      <div class="seg" data-seg="zone" role="group" aria-label="Zone">
        <button type="button" data-v="all" aria-pressed="true">All ${regionCount}</button>
        ${ZONE_ORDER.map((z) => `<button type="button" data-v="${esc(z)}" aria-pressed="false">${esc(z)}</button>`).join('')}
      </div></div>
    <div class="control" style="flex:1 1 240px">
      <label class="field-label" for="trails-region">Follow one region</label>
      <select id="trails-region" data-sel="region"></select></div>
    <button type="button" class="pill pill-clear truncate" data-slot="focus-pill" hidden></button>
  </div>

  <div class="card" data-slot="chart-card">
    <h2 class="card-title">Where each region walked</h2>
    <p class="card-sub" data-slot="chart-sub"></p>
    <div class="chart-frame" data-slot="chart-frame" style="margin-top:var(--space-md)"></div>
    <div data-slot="legend"></div>
    <div data-slot="readout" style="margin-top:var(--space-lg)"></div>
    <p class="xsmall muted" style="margin-top:var(--space-sm)">Scroll or pinch to zoom, drag to pan, double-click to reset.
      Click a thread to follow it; click a dot on a followed thread to open that region year by year.</p>
  </div>

  <div class="card" style="margin-top:var(--space-xl)">
    <h3 class="card-title">How far the land pulled ahead</h3>
    <p class="card-sub">For each region, the multiple the land value grew by, divided by the multiple the cash receipts grew by,
      from that region's own first surveyed year to ${fy(settledLast)}. A bar to the right of the line means the land outran the
      farming; a bar to the left would mean the farming outran the land.</p>
    <div class="chart-frame" data-slot="rank-frame" style="margin-top:var(--space-md)"></div>
    <div class="stack" data-slot="rank-note" style="margin-top:var(--space-lg)"></div>
  </div>

  <div style="margin-top:var(--space-xl)" data-slot="back-note"></div>

  <p class="xsmall muted" style="margin-top:var(--space-xl)">${esc(SCOPE_SHORT)}</p>`;
}

/**
 * Build the per-year points.
 *
 * Iterates the SETTLED YEAR LIST, not the region's own rows, so a year ABARES
 * never published becomes an explicit null and breaks the thread. Walking the
 * rows instead would quietly join 1998 to 2001 with a straight line.
 *
 * The three NT regions that start in 1992 need no special case here: their
 * leading years are simply null and the thread starts where their data does.
 */
function pointsFor(rows: TrailYear[], years: number[], measure: Measure): Pt[] {
  const byYear = new Map<number, TrailYear>(rows.map((r) => [r.year, r]));
  const out: Pt[] = [];
  for (const y of years) {
    const r = byYear.get(y);
    if (!r) { out.push({ year: y, vx: null, vy: null, tier: null, sample: null, rse: null }); continue; }
    const div = measure === 'ha' ? r.area : 1;
    const ok = div != null && Number.isFinite(div) && div > 0;
    const d = ok ? (div as number) : 1;
    out.push({
      year: y,
      vx: ok && r.landValue != null && r.landValue > 0 ? r.landValue / d : null,
      vy: ok && r.receipts != null && r.receipts > 0 ? r.receipts / d : null,
      tier: r.tier, sample: r.sample, rse: r.rateExRse,
    });
  }
  return out;
}

/** Points in pixels. Either coordinate missing nulls the pair, so the line breaks. */
function toPixels(pts: Pt[], sx: Scale, sy: Scale): PxPt[] {
  return pts.map((p) => {
    const x = p.vx != null ? sx(p.vx) : NaN;
    const y = p.vy != null ? sy(p.vy) : NaN;
    const ok = Number.isFinite(x) && Number.isFinite(y);
    return { x: ok ? x : 0, y: ok ? y : null, p };
  });
}

/**
 * Two log scales carrying the SAME number of pixels per decade.
 *
 * Each domain is padded, then whichever spans fewer decades is widened about
 * its own centre until the two spans match. Without this the diagonals below are
 * not 45° and "grew in step" stops being readable off the page.
 */
function squareLogScales(xs: (number | null)[], ys: (number | null)[]): [Scale, Scale] {
  const lx = extent(xs.map((v) => (v != null && v > 0 ? Math.log10(v) : null)));
  const ly = extent(ys.map((v) => (v != null && v > 0 ? Math.log10(v) : null)));
  const pad = 0.08;
  let [x0, x1] = [lx[0] - pad, lx[1] + pad];
  let [y0, y1] = [ly[0] - pad, ly[1] + pad];
  // logScale silently forces at least one decade; do it here so the two spans
  // stay equal rather than being quietly pulled apart inside the helper.
  const span = Math.max(1, x1 - x0, y1 - y0);
  const centre = (a: number, b: number) => (a + b) / 2;
  const cx = centre(x0, x1); const cy = centre(y0, y1);
  [x0, x1] = [cx - span / 2, cx + span / 2];
  [y0, y1] = [cy - span / 2, cy + span / 2];
  return [
    logScale([10 ** x0, 10 ** x1], [X0, X1]),
    logScale([10 ** y0, 10 ** y1], [Y1, Y0]),
  ];
}

/** Nice constant-ratio values (land = k × receipts) spanning the drawn data. */
function ratioLines(xs: (number | null)[], ys: (number | null)[]): number[] {
  const ratios: number[] = [];
  for (let i = 0; i < xs.length; i++) {
    const a = xs[i]; const b = ys[i];
    if (a != null && b != null && a > 0 && b > 0) ratios.push(a / b);
  }
  if (!ratios.length) return [];
  const [lo, hi] = extent(ratios);
  const out: number[] = [];
  for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e++) {
    for (const m of [1, 2, 5]) {
      const v = m * 10 ** e;
      if (v >= lo * 0.6 && v <= hi * 1.6) out.push(v);
    }
  }
  return out;
}

/** The visible segment of the line "land = k × receipts", clipped to the plot. */
function diagonal(k: number, sx: Scale, sy: Scale): { x1: number; y1: number; x2: number; y2: number } | null {
  const lk = Math.log10(k);
  const lx0 = Math.log10(sx.domain[0]); const lx1 = Math.log10(sx.domain[1]);
  const ly0 = Math.log10(sy.domain[0]); const ly1 = Math.log10(sy.domain[1]);
  const a = Math.max(lx0, ly0 + lk);
  const b = Math.min(lx1, ly1 + lk);
  if (!(b > a)) return null;
  const p1x = sx(10 ** a); const p1y = sy(10 ** (a - lk));
  const p2x = sx(10 ** b); const p2y = sy(10 ** (b - lk));
  if (![p1x, p1y, p2x, p2y].every((n) => Number.isFinite(n))) return null;
  return { x1: p1x, y1: p1y, x2: p2x, y2: p2y };
}

/** A waypoint drawn to its tier: solid, hollow, or a fixed token that never sizes. */
function waypoint(cx: number, cy: number, t: Tier | null, hue: string): SVGElement {
  if (isTokenSized(t)) {
    return el('rect', {
      x: cx - 3.5, y: cy - 3.5, width: 7, height: 7, fill: 'none',
      stroke: hue, 'stroke-width': 1.2, opacity: tierOpacity(t),
    });
  }
  if (isOutlineOnly(t)) {
    return el('circle', { cx, cy, r: 3.6, fill: 'none', stroke: hue, 'stroke-width': 1.5, opacity: tierOpacity(t) });
  }
  return el('circle', { cx, cy, r: 3.2, fill: hue, stroke: 'var(--bg-panel)', 'stroke-width': 0.8, opacity: tierOpacity(t) });
}

function arrowHead(x1: number, y1: number, x2: number, y2: number, hue: string, op: number): SVGElement | null {
  const a = Math.atan2(y2 - y1, x2 - x1);
  if (!Number.isFinite(a)) return null;
  const L = 9; const Wd = 4.4;
  const rot = ([px, py]: [number, number]): [number, number] => [
    x2 + px * Math.cos(a) - py * Math.sin(a),
    y2 + px * Math.sin(a) + py * Math.cos(a),
  ];
  const pts: Array<[number, number]> = [[2, 0], [-L, Wd], [-L, -Wd]].map((p) => rot(p as [number, number]));
  if (!pts.every(([px, py]) => Number.isFinite(px) && Number.isFinite(py))) return null;
  return el('polygon', { points: pts.map(([px, py]) => `${px.toFixed(2)},${py.toFixed(2)}`).join(' '), fill: hue, opacity: op });
}

/**
 * The growth multiple of land divided by the growth multiple of receipts,
 * measured between the region's OWN first year and the last SETTLED year.
 * 2025 is provisional and never touches this.
 */
function drift(r: TrailRegion, settledLast: number): Drift | null {
  const from = r.series.find((s) => s.year === r.firstYear);
  const to = r.series.find((s) => s.year === settledLast);
  if (!from || !to) return null;
  const l0 = from.landValue; const l1 = to.landValue;
  const r0 = from.receipts; const r1 = to.receipts;
  if (l0 == null || l1 == null || r0 == null || r1 == null) return null;
  if (!(l0 > 0 && l1 > 0 && r0 > 0 && r1 > 0)) return null;
  const land = l1 / l0; const rec = r1 / r0;
  if (!Number.isFinite(land) || !Number.isFinite(rec) || rec === 0) return null;
  const d = land / rec;
  if (!Number.isFinite(d)) return null;
  return { region: r, d, land, rec, from, to };
}

function countBackwardSteps(regions: TrailRegion[], settledLast: number): { back: number; total: number } {
  let back = 0; let total = 0;
  for (const r of regions) {
    const s = r.series.filter((y) => y.year <= settledLast);
    for (let i = 1; i < s.length; i++) {
      const a = s[i - 1].landValue; const b = s[i].landValue;
      if (a == null || b == null) continue;
      total++;
      if (b < a) back++;
    }
  }
  return { back, total };
}

function linearRank(lo: number, hi: number): Scale {
  const span = hi - lo || 1;
  const fn = ((v: number) => RANK_BAR0 + ((v - lo) / span) * (RANK_BAR1 - RANK_BAR0)) as Scale;
  fn.domain = [lo, hi];
  fn.range = [RANK_BAR0, RANK_BAR1];
  fn.invert = (px: number) => lo + ((px - RANK_BAR0) / (RANK_BAR1 - RANK_BAR0)) * span;
  fn.ticks = (count = 5) => niceTicks(lo, hi, count);
  return fn;
}

function thin(ticks: number[]): number[] {
  if (ticks.length <= 9) return ticks;
  const step = Math.ceil(ticks.length / 9);
  return ticks.filter((_, i) => i % step === 0);
}

function formatRatio(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  return a >= 100 ? String(Math.round(v)) : a >= 10 ? v.toFixed(1) : v.toFixed(2);
}

function valueText(v: number | null, t?: Tier | null): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const prefix = t != null ? tierPrefix(t) : '';
  return `${prefix}${v >= 1000 ? money(v) : `$${v.toFixed(v >= 100 ? 0 : 1)}`}`;
}

function shorten(name: string): string {
  // truncEsc escapes for HTML; SVG text is set via textContent, so undo nothing
  // — just take the plain truncation and let textContent do the escaping.
  const t = truncEsc(name, 30);
  return t.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

function clusters(years: number[]): string {
  if (!years.length) return '';
  const sorted = [...years].sort((a, b) => a - b);
  const runs: number[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1] + 1) runs[runs.length - 1].push(sorted[i]);
    else runs.push([sorted[i]]);
  }
  return runs.map((r) => (r.length > 1 ? `${fy(r[0])} to ${fy(r[r.length - 1])}` : fy(r[0]))).join(' and ');
}

function enterSpace(fn: () => void): (e: KeyboardEvent) => void {
  return (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    fn();
  };
}
